//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useState, useContext } from 'react';
import { makeStyles } from '@material-ui/core';
import { differenceInHours } from 'date-fns';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
// import { unsubscribeRealtimeUpdates } from '../../system/firebase';
// import { LayoutContext, AppContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import {
    CONFIRM_DIALOG_ADDITIONAL_SEAT_CANCELATION_HEADING,
    CONFIRM_DIALOG_ADDITIONAL_SEAT_CANCELATION_MESSAGE,
    CONFIRM_DIALOG_ADDITIONAL_SEAT_ELIGIBLE_FOR_REFUND_HEADING,
    CONFIRM_DIALOG_ADDITIONAL_SEAT_ELIGIBLE_FOR_REFUND_MESSAGE,
} from '../../common/constants';
import { LayoutContext, AppContext } from '../../common/contexts';
// import { retrieveStripSubscription } from '../../common/services/paymentServices';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { cancelAdditionalSeatSubscription } from '../../common/services/empMySettingsServices';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdEditDialog from '../../common/elemental/AfitdEditDialog';
import AfitdMessageDialog from '../../common/elemental/AfitdMessageDialog';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
import _get from 'lodash/get';
import _noop from 'lodash/noop';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    return makeStyles(theme => ({
        subtitle: { fontWeight: 'bold' },

        // lineSeprator: { borderTop: '2px solid rgb(240,242,246)', margin: '20px 0' },

        hidden: { display: 'none' },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        props,
        handleClickSaveButton,
        eligibleRefundMessageDialogOpen,
        // setEligibleRefundMessageDialogOpen,
        cancelScheduledMessageDialogOpen,
        // setCancelScheduledMessageDialogOpen,
        handleClickEligibleRefundMessageDialogOk,
        handleClickCancelScheduledMessageDialogOk,
    } = params;
    const { open, onClose = _noop } = props;

    const defns = {
        container: {
            open,
            onClose,
            label: `Cancel Additional Seats?`,
            actionButtonDefns: {
                save: {
                    disabled: false,
                    color: 'default',
                    onClick: handleClickSaveButton,
                    label: 'Yes',
                },
                cancel: { label: 'No' },
            },
        },
        subtitle: { variant: 'body2', value: 'Hold up, there might be a better way!' },

        eligibleRefundMessageDialog: {
            title: CONFIRM_DIALOG_ADDITIONAL_SEAT_ELIGIBLE_FOR_REFUND_HEADING,
            message: CONFIRM_DIALOG_ADDITIONAL_SEAT_ELIGIBLE_FOR_REFUND_MESSAGE,
            open: eligibleRefundMessageDialogOpen,
            onClose: handleClickEligibleRefundMessageDialogOk,
        },
        cancelScheduledMessageDialog: {
            title: CONFIRM_DIALOG_ADDITIONAL_SEAT_CANCELATION_HEADING,
            message: CONFIRM_DIALOG_ADDITIONAL_SEAT_CANCELATION_MESSAGE,
            open: cancelScheduledMessageDialogOpen,
            onClose: handleClickCancelScheduledMessageDialogOk,
        },
    };

    setClassNameInDefns(defns, classes);
    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
export default function CancelAdditionalSeatDialog(props) {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles(/*{param1, param2}*/)();

    const appContext = useContext(AppContext);
    const { currentEmployer, employerId } = appContext;

    const layoutContext = useContext(LayoutContext);
    const { setSnackbarMessage, setSnackbarErrorMessage } = layoutContext;

    const stripeSubId = _get(currentEmployer, 'stripeSubId');
    const stripeCusCreateDt = _get(currentEmployer, 'stripeCusCreateDt');
    const stripeAdditionalSeatSubId = _get(currentEmployer, 'stripeAdditionalSeatSubId');

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { additionalSeats, onClose = _noop } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [eligibleRefundMessageDialogOpen, setEligibleRefundMessageDialogOpen] = useState(false);
    const [cancelScheduledMessageDialogOpen, setCancelScheduledMessageDialogOpen] = useState(false);
    // const [stripeSubscription, setStripeSubscription] = useState();

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    // const stripeSubscriptionCreated = stripeSubscription?.created;
    // const stripeSubscriptionCreateDt = stripeSubscriptionCreated ? new Date(stripeSubscriptionCreated * 1000) : null;

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    // // load Stripe subscription data for Tier
    // useEffect(() => {
    //     const retrieveSubscription = async () => {
    //         const retrieveSubscriptionResp = await retrieveStripSubscription({ subscriptionId: stripeSubId });
    //         retrieveSubscriptionResp && setStripeSubscription(retrieveSubscriptionResp.data);
    //     };
    //     if (open && stripeSubId) {
    //         retrieveSubscription();
    //     } else {
    //         setStripeSubscription(null);
    //     }
    // }, [open, stripeSubId]);

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const handleClickSaveButton = async () => {
        const hoursDifference = differenceInHours(new Date(), stripeCusCreateDt);
        const isEligibleRefund = hoursDifference < 48;

        const success = await cancelAdditionalSeatSubscription({
            isRefund: isEligibleRefund,
            stripeSubId,
            stripeAdditionalSeatSubId,
            employerId,
            setSnackbarMessage,
            setSnackbarErrorMessage,
        });

        if (success) {
            if (isEligibleRefund) setEligibleRefundMessageDialogOpen(true);
            else setCancelScheduledMessageDialogOpen(true);
        }
    };

    const handleClickEligibleRefundMessageDialogOk = () => {
        setEligibleRefundMessageDialogOpen(false);
        handleCloseDialog();
    };

    const handleClickCancelScheduledMessageDialogOk = () => {
        setCancelScheduledMessageDialogOpen(false);
        handleCloseDialog();
    };

    const handleCloseDialog = () => {
        onClose && onClose();
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({
        classes,
        props,
        handleClickSaveButton,
        eligibleRefundMessageDialogOpen,
        setEligibleRefundMessageDialogOpen,
        cancelScheduledMessageDialogOpen,
        setCancelScheduledMessageDialogOpen,
        handleClickEligibleRefundMessageDialogOk,
        handleClickCancelScheduledMessageDialogOk,
        handleCloseDialog,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <AfitdEditDialog {...defns.container}>
            <AfitdTypography {...defns.message}>
                {additionalSeats} additional seat{additionalSeats === 1 ? '' : 's'} will be canceled from your monthly plan.
            </AfitdTypography>

            {/* <DowngradeDialog {...defns.downgradeModal} /> */}
            {/* <ConfirmDowngradeDialog {...defns.confirmDowngradeModal} /> */}
            <AfitdMessageDialog {...defns.eligibleRefundMessageDialog} />
            <AfitdMessageDialog {...defns.cancelScheduledMessageDialog} />
        </AfitdEditDialog>
    );
}
